import AddOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import Button from "@mui/material/Button"
import InputAdornment from "@mui/material/InputAdornment"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column, { PaddedContentColumn } from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import { RowButColumnOnMobile } from "../../../common/components/Row"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  HeaderTableCell,
  NoWrapTableCell,
  NumberTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../common/components/tables/cells"
import {
  getTableSearchParams,
  usePaginationState,
  useTableSearchTextState,
  useTableSort,
} from "../../../common/components/tables/utils"
import { formatCurrency, trimLongString } from "../../../common/utils"
import { selectProfileLoaded } from "../../users/reducer"
import BorderedGrayChip from "../components/BorderedGrayChip"
import CandidateAvatar from "../components/CandidateAvatar"
import CreateOfferDialog from "../components/offers/CreateOfferDialog"
import { OFFER_STATUS_CHOICES } from "../utils"
import { commissionRatioDisplay } from "../utils"
import CreateOfferSelectCandidateDialog from "./CreateOfferSelectCandidateDialog"
import OfferDetailsDialog from "./OfferDetailsDialog"

export default function OfferList() {
  const [offers, setOffers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [
    createOfferSelectCandidateDialogOpen,
    setCreateOfferSelectCandidateDialogOpen,
  ] = useState(false)
  const { sortColumn, sortDirection, serverSort, onSortColumnChanged } =
    useTableSort()
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState(20)
  const [searchText, setSearchText, debouncedSearchText] =
    useTableSearchTextState("q", setPageIndex)
  const [clickedOffer, setClickedOffer] = useState(null)
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [createOfferDialogOpen, setCreateOfferDialogOpen] = useState(false)
  const axios = useAuthorizedAxiosClient()
  const profileLoaded = useSelector(selectProfileLoaded)
  const candidateID = useSearchParams()[0].get("cid")

  function handleSearchTextChanged(e) {
    setSearchText(e.target.value)
    setPageIndex(1)
  }

  function handleCreateOfferSelectCandidateDialogClosed(selectedCandidate) {
    if (selectedCandidate) {
      setSelectedCandidate(selectedCandidate)
      setCreateOfferDialogOpen(true)
    }
    setCreateOfferSelectCandidateDialogOpen(false)
  }

  function handleCreateOfferDialogClosed(createdOffer) {
    if (createdOffer) {
      setOffers([{ ...createdOffer, candidate: selectedCandidate }, ...offers])
    }
    setSelectedCandidate(null)
    setCreateOfferDialogOpen(false)
  }

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      setIsLoading(true)
      let url = "/crm/api/offers/"
      if (candidateID) {
        url += `candidate/${candidateID}/`
      }
      axios
        .get(url, {
          params: getTableSearchParams(
            debouncedSearchText,
            pageIndex,
            pageSize,
            serverSort
          ),
        })
        .then(({ data }) => {
          setOffers(data.results)
          setTotalResultsSize(data.count)
        })
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      profileLoaded,
      candidateID,
      debouncedSearchText,
      serverSort,
      pageIndex,
      pageSize,
    ]
  )

  return (
    <Column>
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <PageTopSection title="Proposals" />
      <PaddedContentColumn>
        <RowButColumnOnMobile
          sx={{
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <TextField
            placeholder="Search"
            value={searchText}
            onChange={handleSearchTextChanged}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { sm: "280px" } }}
          />
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setCreateOfferSelectCandidateDialogOpen(true)}
          >
            Create Proposal
          </Button>
          <CreateOfferSelectCandidateDialog
            open={createOfferSelectCandidateDialogOpen}
            onClose={handleCreateOfferSelectCandidateDialogClosed}
          />
          <CreateOfferDialog
            candidate={selectedCandidate}
            open={createOfferDialogOpen}
            onClose={handleCreateOfferDialogClosed}
          />
        </RowButColumnOnMobile>
        <ScrollableTableContainer>
          <Table sx={{ mt: 3 }}>
            <TableHead>
              <TableRow>
                <HeaderTableCell>{/* Avatar */}</HeaderTableCell>
                <SortableHeaderCell
                  columnName="candidate.candidate_name"
                  label="Candidate name"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                  HeaderCellComponent={StickyHeaderTableCell}
                />
                <SortableHeaderCell
                  columnName="template.name"
                  label="Name"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  columnName="status"
                  label="Status"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  columnName="template.signing_bonus"
                  label="Signing bonus"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  columnName="template.commission_tiers"
                  label="Commission split"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  columnName="template.per_transaction_fee"
                  label="Transaction fee"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  columnName="template.monthly_desk_fee"
                  label="Desk fee"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
              </TableRow>
            </TableHead>
            <TableBody sx={{ cursor: "pointer" }}>
              {(isLoading || !profileLoaded) && offers.length === 0 ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRow key={index}>
                    <NoWrapTableCell>
                      <Skeleton variant="circular" width={32} height={32} />
                    </NoWrapTableCell>
                    <StickyTableCell>
                      <Skeleton variant="text" width={150} />
                    </StickyTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={120} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="rectangular" width={80} height={24} />
                    </NoWrapTableCell>
                    <NumberTableCell>
                      <Skeleton variant="text" width={80} />
                    </NumberTableCell>
                    <NumberTableCell>
                      <Skeleton variant="text" width={100} />
                    </NumberTableCell>
                    <NumberTableCell>
                      <Skeleton variant="text" width={80} />
                    </NumberTableCell>
                    <NumberTableCell>
                      <Skeleton variant="text" width={80} />
                    </NumberTableCell>
                  </TableRow>
                ))
              ) : offers.length === 0 ? (
                <NoResultsTableRow colSpan={8} />
              ) : (
                offers.map((offer) => (
                  <TableRow
                    key={offer.id}
                    onClick={() => setClickedOffer(offer)}
                  >
                    <NoWrapTableCell>
                      <CandidateAvatar
                        candidate={offer.candidate}
                        size="32px"
                        fontSize="1em"
                      />
                    </NoWrapTableCell>
                    <StickyTableCell title={offer.candidate.candidate_name}>
                      {trimLongString(offer.candidate.candidate_name)}
                    </StickyTableCell>
                    <NoWrapTableCell title={offer.template.name}>
                      {trimLongString(offer.template.name)}
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <BorderedGrayChip
                        label={OFFER_STATUS_CHOICES[offer.status]}
                      />
                    </NoWrapTableCell>
                    <NumberTableCell>
                      {formatCurrency(offer.template.signing_bonus)}
                    </NumberTableCell>
                    <NumberTableCell>
                      {commissionRatioDisplay(offer.template.commission_tiers)}
                    </NumberTableCell>
                    <NumberTableCell>
                      {formatCurrency(offer.template.per_transaction_fee)}
                    </NumberTableCell>
                    <NumberTableCell>
                      {formatCurrency(offer.template.monthly_desk_fee)}
                    </NumberTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <OfferDetailsDialog
              open={!!clickedOffer}
              onClose={() => setClickedOffer(null)}
              offer={clickedOffer}
            />
          </Table>
        </ScrollableTableContainer>
        <TablePaginationRow
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
          tableRows={offers}
          totalResultsSize={totalResultsSize}
        />
      </PaddedContentColumn>
    </Column>
  )
}
